'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { RefreshCw, Search, X } from 'lucide-react';
import {
  getLiveWorldCupFixtures,
  getUpcomingFixtures,
  getStatusLabel,
  isInPlay,
  getFlag,
  type LiveFixture,
} from '@/lib/api-football';
import { subscribeToScoreUpdates, broadcastScoreUpdate, type ScoreUpdatePayload } from '@/lib/supabase-realtime';
import { useLanguage } from '@/contexts/LanguageContext';

// --- Countdown Hook ---
function useCountdown(targetDate: Date) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const targetTime = targetDate.getTime();

  useEffect(() => {
    function calculate() {
      const now = Date.now();
      const diff = targetTime - now;
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    }
    calculate();
    const id = setInterval(calculate, 1000);
    return () => clearInterval(id);
  }, [targetTime]);

  return timeLeft;
}

// --- Countdown Unit ---
function CountUnit({ value, label }: { value: number; label: string }) {
  const display = String(value).padStart(2, '0');
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center rounded-xl font-mono text-2xl sm:text-3xl font-black text-white"
        style={{ background: 'hsl(222 35% 14%)', border: '1px solid hsl(189 94% 51% / 0.25)', boxShadow: '0 0 16px hsl(189 94% 51% / 0.1)' }}
      >
        {display}
      </div>
      <span className="text-xs font-medium uppercase tracking-widest text-slate-500">{label}</span>
    </div>
  );
}

// --- Hero Banner ---
function WorldCupHeroBanner() {
  const KICKOFF = new Date('2026-06-11T18:00:00Z');
  const { days, hours, minutes, seconds } = useCountdown(KICKOFF);
  const { t } = useLanguage();

  return (
    <div
      className="relative overflow-hidden rounded-2xl p-8 sm:p-12 flex flex-col items-center text-center gap-6"
      style={{
        background: 'linear-gradient(135deg, hsl(222 40% 9%) 0%, hsl(222 35% 13%) 50%, hsl(222 40% 9%) 100%)',
        border: '1px solid hsl(189 94% 51% / 0.2)',
        boxShadow: '0 0 60px hsl(189 94% 51% / 0.06), inset 0 1px 0 hsl(189 94% 51% / 0.08)',
      }}
    >
      {/* Decorative glow orbs */}
      <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, hsl(189 94% 51% / 0.08) 0%, transparent 70%)' }} />
      <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, hsl(271 91% 65% / 0.07) 0%, transparent 70%)' }} />

      {/* Badge */}
      <div
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest"
        style={{ background: 'hsl(189 94% 51% / 0.1)', color: 'hsl(189 94% 65%)', border: '1px solid hsl(189 94% 51% / 0.2)' }}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
        FIFA World Cup 2026
      </div>

      {/* Headline */}
      <div className="space-y-2">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight tracking-tight">
          World Cup 2026
        </h2>
        <p className="text-lg sm:text-xl font-semibold" style={{ color: 'hsl(189 94% 65%)' }}>
          {t.dashboard.worldCupKicksOff} <span className="text-white">June 11</span>
        </p>
        <p className="text-sm text-slate-500 max-w-sm mx-auto">
          {t.dashboard.worldCupNoFixtures}
        </p>
      </div>

      {/* Countdown */}
      <div className="flex items-start gap-3 sm:gap-5">
        <CountUnit value={days} label={t.common.days} />
        <span className="text-2xl font-black text-slate-600 mt-4">:</span>
        <CountUnit value={hours} label={t.common.hours} />
        <span className="text-2xl font-black text-slate-600 mt-4">:</span>
        <CountUnit value={minutes} label={t.common.mins} />
        <span className="text-2xl font-black text-slate-600 mt-4">:</span>
        <CountUnit value={seconds} label={t.common.secs} />
      </div>

      {/* Sub-label */}
      <p className="text-xs text-slate-600 font-mono">
        {t.dashboard.worldCupCountdownSub}
      </p>
    </div>
  );
}

// --- Status Badge ---
function StatusBadge({ status }: { status: LiveFixture['fixture']['status'] }) {
  const label = getStatusLabel(status);
  const inPlay = isInPlay(status);
  const isHT = status.short === 'HT';

  if (isHT) {
    return (
      <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'hsl(38 92% 50% / 0.15)', color: 'hsl(38 92% 65%)' }}>
        HT
      </span>
    );
  }
  if (inPlay) {
    return (
      <span className="flex items-center gap-1.5 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'hsl(0 84% 60% / 0.12)', color: 'hsl(0 84% 70%)' }}>
        <span className="w-1.5 h-1.5 rounded-full bg-red-400 live-pulse" />
        {label}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'hsl(189 94% 51% / 0.12)', color: 'hsl(189 94% 65%)' }}>
      {label}
    </span>
  );
}

// --- Match Card ---
function MatchCard({ match }: { match: LiveFixture }) {
  const elapsed = match.fixture.status.elapsed;
  const inPlay = isInPlay(match.fixture.status);
  const isHT = match.fixture.status.short === 'HT';
  const homeScore = match.goals.home ?? 0;
  const awayScore = match.goals.away ?? 0;
  const { t } = useLanguage();

  return (
    <Link href={`/game-detail?id=${match.fixture.id}`}>
      <div
        className="rounded-xl p-4 card-hover cursor-pointer group transition-all duration-200"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
      >
        {/* Header row */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-slate-500 truncate max-w-[60%]">
            {match.league.round}
          </span>
          <StatusBadge status={match.fixture.status} />
        </div>

        {/* Teams + Score */}
        <div className="flex items-center justify-between gap-2">
          {/* Home team */}
          <div className="flex flex-col items-center gap-1.5 flex-1">
            {match.teams.home.logo ? (
              <img src={match.teams.home.logo} alt={match.teams.home.name} className="w-9 h-9 object-contain" />
            ) : (
              <span className="text-2xl">{getFlag(match.teams.home.name)}</span>
            )}
            <span className="text-xs font-semibold text-slate-300 text-center truncate w-full leading-tight">
              {match.teams.home.name}
            </span>
          </div>

          {/* Score block */}
          <div className="flex flex-col items-center gap-0.5 px-2 min-w-[72px]">
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-2xl font-black text-white tabular-nums">{homeScore}</span>
              <span className="text-slate-600 text-lg">–</span>
              <span className="font-mono text-2xl font-black text-white tabular-nums">{awayScore}</span>
            </div>
            {/* Elapsed minute display */}
            {(inPlay || isHT) && elapsed !== null && (
              <span
                className="text-xs font-bold tabular-nums px-2 py-0.5 rounded-full"
                style={{
                  background: isHT ? 'hsl(38 92% 50% / 0.12)' : 'hsl(0 84% 60% / 0.1)',
                  color: isHT ? 'hsl(38 92% 65%)' : 'hsl(0 84% 70%)',
                }}
              >
                {isHT ? 'HT' : `${elapsed}'`}
              </span>
            )}
            {match.fixture.venue.name && (
              <span className="text-xs text-slate-600 truncate max-w-[80px] text-center mt-0.5">
                {match.fixture.venue.name}
              </span>
            )}
          </div>

          {/* Away team */}
          <div className="flex flex-col items-center gap-1.5 flex-1">
            {match.teams.away.logo ? (
              <img src={match.teams.away.logo} alt={match.teams.away.name} className="w-9 h-9 object-contain" />
            ) : (
              <span className="text-2xl">{getFlag(match.teams.away.name)}</span>
            )}
            <span className="text-xs font-semibold text-slate-300 text-center truncate w-full leading-tight">
              {match.teams.away.name}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-3 pt-2 flex items-center justify-end" style={{ borderTop: '1px solid hsl(222 25% 14%)' }}>
          <span className="text-xs text-slate-600 group-hover:text-cyan-400 transition-colors">{t.common.viewDetails}</span>
        </div>
      </div>
    </Link>
  );
}

// --- Next Match Countdown Banner ---
function NextMatchCountdownBanner({ fixture }: { fixture: LiveFixture }) {
  const kickoff = new Date(fixture.fixture.date);
  const { days, hours, minutes, seconds } = useCountdown(kickoff);
  const isImminent = days === 0 && hours === 0 && minutes <= 30;
  const isVeryClose = days === 0 && hours === 0 && minutes <= 10;
  const { t } = useLanguage();

  const homeTeam = fixture.teams.home;
  const awayTeam = fixture.teams.away;

  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 sm:p-6 mb-4"
      style={{
        background: isVeryClose
          ? 'linear-gradient(135deg, hsl(0 84% 8%) 0%, hsl(222 35% 11%) 60%, hsl(0 84% 8%) 100%)'
          : isImminent
          ? 'linear-gradient(135deg, hsl(38 92% 8%) 0%, hsl(222 35% 11%) 60%, hsl(38 92% 8%) 100%)'
          : 'linear-gradient(135deg, hsl(222 40% 9%) 0%, hsl(222 35% 13%) 60%, hsl(222 40% 9%) 100%)',
        border: isVeryClose
          ? '1px solid hsl(0 84% 60% / 0.35)'
          : isImminent
          ? '1px solid hsl(38 92% 50% / 0.3)'
          : '1px solid hsl(189 94% 51% / 0.2)',
        boxShadow: isVeryClose
          ? '0 0 40px hsl(0 84% 60% / 0.08)'
          : isImminent
          ? '0 0 40px hsl(38 92% 50% / 0.07)'
          : '0 0 40px hsl(189 94% 51% / 0.05)',
      }}
    >
      {/* Decorative glow */}
      <div
        className="absolute -top-16 -right-16 w-48 h-48 rounded-full pointer-events-none"
        style={{
          background: isVeryClose
            ? 'radial-gradient(circle, hsl(0 84% 60% / 0.1) 0%, transparent 70%)'
            : isImminent
            ? 'radial-gradient(circle, hsl(38 92% 50% / 0.09) 0%, transparent 70%)'
            : 'radial-gradient(circle, hsl(189 94% 51% / 0.07) 0%, transparent 70%)',
        }}
      />

      {/* Attention badge */}
      <div className="flex items-center gap-2 mb-4">
        <div
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
          style={
            isVeryClose
              ? { background: 'hsl(0 84% 60% / 0.15)', color: 'hsl(0 84% 72%)', border: '1px solid hsl(0 84% 60% / 0.3)' }
              : isImminent
              ? { background: 'hsl(38 92% 50% / 0.12)', color: 'hsl(38 92% 65%)', border: '1px solid hsl(38 92% 50% / 0.25)' }
              : { background: 'hsl(189 94% 51% / 0.1)', color: 'hsl(189 94% 65%)', border: '1px solid hsl(189 94% 51% / 0.2)' }
          }
        >
          <span
            className="w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ background: isVeryClose ? 'hsl(0 84% 65%)' : isImminent ? 'hsl(38 92% 60%)' : 'hsl(189 94% 60%)' }}
          />
          {isVeryClose ? `⚠️ ${t.dashboard.kickoffImminent}` : isImminent ? `🔔 ${t.dashboard.matchStartingSoon}` : `⏱ ${t.dashboard.nextMatch}`}
        </div>
      </div>

      {/* Attention message */}
      <p className="text-sm font-semibold mb-4" style={{ color: isVeryClose ? 'hsl(0 84% 72%)' : isImminent ? 'hsl(38 92% 65%)' : 'hsl(189 94% 65%)' }}>
        {isVeryClose
          ? t.dashboard.kickoffImminentMsg
          : isImminent
          ? t.dashboard.matchStartingSoonMsg
          : t.dashboard.noMatchesLiveMsg}
      </p>

      {/* Teams */}
      <div className="flex items-center justify-center gap-4 mb-5">
        <div className="flex flex-col items-center gap-1.5 flex-1 max-w-[100px]">
          {homeTeam.logo ? (
            <img src={homeTeam.logo} alt={homeTeam.name} className="w-10 h-10 object-contain" />
          ) : (
            <span className="text-3xl">{getFlag(homeTeam.name)}</span>
          )}
          <span className="text-xs font-bold text-white text-center truncate w-full">{homeTeam.name}</span>
        </div>
        <div className="flex flex-col items-center gap-1 px-3">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-widest">{fixture.league.round}</span>
          <span className="text-lg font-black text-slate-500">VS</span>
          {fixture.fixture.venue.name && (
            <span className="text-xs text-slate-600 text-center max-w-[120px] truncate">{fixture.fixture.venue.city ?? fixture.fixture.venue.name}</span>
          )}
        </div>
        <div className="flex flex-col items-center gap-1.5 flex-1 max-w-[100px]">
          {awayTeam.logo ? (
            <img src={awayTeam.logo} alt={awayTeam.name} className="w-10 h-10 object-contain" />
          ) : (
            <span className="text-3xl">{getFlag(awayTeam.name)}</span>
          )}
          <span className="text-xs font-bold text-white text-center truncate w-full">{awayTeam.name}</span>
        </div>
      </div>

      {/* Countdown */}
      <div className="flex items-start justify-center gap-2 sm:gap-4">
        <CountUnit value={days} label={t.common.days} />
        <span className="text-xl font-black text-slate-600 mt-4">:</span>
        <CountUnit value={hours} label={t.common.hours} />
        <span className="text-xl font-black text-slate-600 mt-4">:</span>
        <CountUnit value={minutes} label={t.common.mins} />
        <span className="text-xl font-black text-slate-600 mt-4">:</span>
        <CountUnit value={seconds} label={t.common.secs} />
      </div>

      {/* Kickoff time label */}
      <p className="text-center text-xs text-slate-600 font-mono mt-3">
        {t.dashboard.kickoffLabel} · {new Date(fixture.fixture.date).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} · {new Date(fixture.fixture.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </p>
    </div>
  );
}

// --- Main Component ---
export default function LiveMatchesGrid() {
  const [matches, setMatches] = useState<LiveFixture[]>([]);
  const [nextFixture, setNextFixture] = useState<LiveFixture | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [teamSearch, setTeamSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'live' | 'ht'>('all');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevMatchesRef = useRef<LiveFixture[]>([]);
  const { t } = useLanguage();

  const fetchMatches = useCallback(async () => {
    try {
      const data = await getLiveWorldCupFixtures();
      if (data && data.length > 0) {
        data.forEach((match) => {
          const prev = prevMatchesRef.current.find(m => m.fixture.id === match.fixture.id);
          if (prev) {
            const prevHome = prev.goals.home ?? 0;
            const prevAway = prev.goals.away ?? 0;
            const curHome = match.goals.home ?? 0;
            const curAway = match.goals.away ?? 0;
            if (prevHome !== curHome || prevAway !== curAway) {
              const payload: ScoreUpdatePayload = {
                fixtureId: match.fixture.id,
                homeScore: curHome,
                awayScore: curAway,
                minute: match.fixture.status.elapsed ?? 0,
                status: match.fixture.status.short,
                homeName: match.teams.home.name,
                awayName: match.teams.away.name,
              };
              broadcastScoreUpdate(payload);
            }
          }
        });
        prevMatchesRef.current = data;
        setMatches(data);
      } else {
        setMatches([]);
      }
    } catch {
      setMatches([]);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, []);

  const fetchNextFixture = useCallback(async () => {
    try {
      const upcoming = await getUpcomingFixtures(1);
      if (upcoming && upcoming.length > 0) {
        setNextFixture(upcoming[0]);
      } else {
        setNextFixture(null);
      }
    } catch {
      setNextFixture(null);
    }
  }, []);

  useEffect(() => {
    fetchMatches();
    fetchNextFixture();
    // Auto-refresh every 60 seconds (1 minute) as requested
    intervalRef.current = setInterval(fetchMatches, 60_000);
    const nextInterval = setInterval(fetchNextFixture, 120_000);

    const unsub = subscribeToScoreUpdates((payload) => {
      setMatches((prev) =>
        prev.map((m) =>
          m.fixture.id === payload.fixtureId
            ? {
                ...m,
                goals: { home: payload.homeScore, away: payload.awayScore },
                fixture: {
                  ...m.fixture,
                  status: { ...m.fixture.status, elapsed: payload.minute, short: payload.status as string },
                },
              }
            : m
        )
      );
    });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      clearInterval(nextInterval);
      if (unsub) unsub();
    };
  }, [fetchMatches, fetchNextFixture]);

  const inPlayMatches = matches.filter(m => isInPlay(m.fixture.status) || m.fixture.status.short === 'HT');

  // Filtered matches based on search + status filter
  const filteredMatches = inPlayMatches.filter((m) => {
    const query = teamSearch.trim().toLowerCase();
    const matchesSearch =
      !query ||
      m.teams.home.name.toLowerCase().includes(query) ||
      m.teams.away.name.toLowerCase().includes(query);
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'ht' && m.fixture.status.short === 'HT') ||
      (statusFilter === 'live' && isInPlay(m.fixture.status) && m.fixture.status.short !== 'HT');
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-400 live-pulse" />
            {t.dashboard.liveMatches}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="rounded-xl p-4 animate-pulse" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)', height: 180 }} />
          ))}
        </div>
      </div>
    );
  }

  // Zero fixtures — show World Cup countdown hero banner
  if (matches.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-400 live-pulse" />
            {t.dashboard.liveMatches}
            <span className="text-xs font-medium px-2 py-0.5 rounded-full text-slate-400" style={{ background: 'hsl(222 25% 18%)' }}>
              0
            </span>
          </h2>
          {lastRefresh && (
            <span className="flex items-center gap-1 text-xs text-slate-600">
              <RefreshCw size={10} />
              <span className="font-mono tabular-nums">{lastRefresh.toLocaleTimeString()}</span>
            </span>
          )}
        </div>
        {nextFixture ? (
          <NextMatchCountdownBanner fixture={nextFixture} />
        ) : (
          <WorldCupHeroBanner />
        )}
      </div>
    );
  }

  // Fixtures available — show match card grid
  return (
    <div>
      {/* Section header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-white flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-400 live-pulse" />
          {t.dashboard.liveMatches}
          <span className="text-xs font-medium px-2 py-0.5 rounded-full text-slate-400" style={{ background: 'hsl(222 25% 18%)' }}>
            {inPlayMatches.length}
          </span>
        </h2>
        {lastRefresh && (
          <span className="flex items-center gap-1 text-xs text-slate-600">
            <RefreshCw size={10} />
            <span className="font-mono tabular-nums">{lastRefresh.toLocaleTimeString()}</span>
          </span>
        )}
      </div>

      {/* Search + Filter controls — only when there are in-play matches */}
      {inPlayMatches.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          {/* Team search */}
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input
              type="text"
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
              placeholder={t.common.searchTeam}
              className="w-full pl-8 pr-8 py-2 text-sm rounded-lg text-white placeholder-slate-500 outline-none focus:ring-1 transition-all"
              style={{
                background: 'hsl(222 35% 11%)',
                border: '1px solid hsl(222 25% 20%)',
                boxShadow: 'none',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'hsl(189 94% 51% / 0.5)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'hsl(222 25% 20%)'; }}
            />
            {teamSearch && (
              <button
                onClick={() => setTeamSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                aria-label="Clear search"
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Status filter pills */}
          <div className="flex items-center gap-1.5 shrink-0">
            {(['all', 'live', 'ht'] as const).map((f) => {
              const labels = { all: t.common.allFilter, live: t.common.live, ht: t.common.htFilter };
              const active = statusFilter === f;
              return (
                <button
                  key={f}
                  onClick={() => setStatusFilter(f)}
                  className="px-3 py-2 text-xs font-semibold rounded-lg transition-all"
                  style={
                    active
                      ? { background: 'hsl(189 94% 51% / 0.15)', color: 'hsl(189 94% 65%)', border: '1px solid hsl(189 94% 51% / 0.35)' }
                      : { background: 'hsl(222 35% 11%)', color: 'hsl(215 20% 50%)', border: '1px solid hsl(222 25% 20%)' }
                  }
                >
                  {labels[f]}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {inPlayMatches.length === 0 ? (
        <div className="space-y-4">
          {nextFixture && <NextMatchCountdownBanner fixture={nextFixture} />}
          <div className="rounded-xl p-8 text-center" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
            <p className="text-slate-400 text-sm">{t.dashboard.noLiveMatches}</p>
            <p className="text-slate-600 text-xs mt-1">{t.dashboard.checkBack}</p>
          </div>
        </div>
      ) : filteredMatches.length === 0 ? (
        <div className="rounded-xl p-8 text-center" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
          <p className="text-slate-400 text-sm">{t.common.noMatchesFound} <span className="text-white font-semibold">&quot;{teamSearch}&quot;</span></p>
          <button
            onClick={() => { setTeamSearch(''); setStatusFilter('all'); }}
            className="mt-2 text-xs font-medium transition-colors"
            style={{ color: 'hsl(189 94% 65%)' }}
          >
            {t.common.clearFilters}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
          {filteredMatches.map((match) => (
            <MatchCard key={match.fixture.id} match={match} />
          ))}
        </div>
      )}
    </div>
  );
}
